import {
    InventoriesWorkingPaper,
    IntangibleAssetsWorkingPaper,
    InvestmentInTaxiCabsAndLicensesWorkingPaper,
} from '@/services/auditService/interface/asset';
import type {PayloadStrategy} from '@/pages/components/audit/adjustment/adapters/types';

// Investment In Taxi Cabs And Licenses — independent strategy instance (ITL-only registry).
// Payload shape matches IA ({ intangibleRows }) but does not share intangibleAssetsStrategy ref.
export const InvestmentInTaxiCabsAndLicensesStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<InvestmentInTaxiCabsAndLicensesWorkingPaper>;
        const annualId = ctx?.annualId;

        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId,
            }));
        };

        const intangibleRows = addAnnualId(Array.isArray(p.intangibleRows) ? p.intangibleRows : []);

        const next: any = {};
        if (intangibleRows.length > 0) next.intangibleRows = intangibleRows;

        return next;
    },
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Inventories strategy (I1): ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const inventoriesStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<InventoriesWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const commonRow = addAnnualId(Array.isArray(p.commonRow) ? p.commonRow : []);
        const testRow = addAnnualId(Array.isArray(p.testRow) ? p.testRow : []);
        
        const next: any = {};
        if (commonRow.length > 0) next.commonRow = commonRow;
        if (testRow.length > 0) next.testRow = testRow;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}

// Property, Plant and Equipment strategy: ensure payload conforms to PropertyPlantEquipmentWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const propertyPlantEquipmentStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as any; // Use any for backward compatibility with legacy rate fields
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const testRow = addAnnualId(Array.isArray(p.testRow) ? p.testRow : []);
        const locationOfPropertyRow = addAnnualId(Array.isArray(p.locationOfPropertyRow) ? p.locationOfPropertyRow : []);
        const commercialAllowanceRow = addAnnualId(Array.isArray(p.commercialAllowanceRow) ? p.commercialAllowanceRow : []);
        const industrialAllowanceRow = addAnnualId(Array.isArray(p.industrialAllowanceRow) ? p.industrialAllowanceRow : []);
        const furnitureFixtureRow = addAnnualId(Array.isArray(p.furnitureFixtureRow) ? p.furnitureFixtureRow : []);
        const computerHardSoftRow = addAnnualId(Array.isArray(p.computerHardSoftRow) ? p.computerHardSoftRow : []);
        const fullPayMotorRow = addAnnualId(Array.isArray(p.fullPayMotorRow) ? p.fullPayMotorRow : []);
        const hirePurchaseMotorRow = addAnnualId(Array.isArray(p.hirePurchaseMotorRow) ? p.hirePurchaseMotorRow : []);
        const machineryRow = addAnnualId(Array.isArray(p.machineryRow) ? p.machineryRow : []);
        const liftEscalatorRow = addAnnualId(Array.isArray(p.liftEscalatorRow) ? p.liftEscalatorRow : []);
        const equipmentRow = addAnnualId(Array.isArray(p.equipmentRow) ? p.equipmentRow : []);
        
        const next: any = {};
        if (testRow.length > 0) next.testRow = testRow;
        if (locationOfPropertyRow.length > 0) next.locationOfPropertyRow = locationOfPropertyRow;
        if (commercialAllowanceRow.length > 0) next.commercialAllowanceRow = commercialAllowanceRow;
        if (industrialAllowanceRow.length > 0) next.industrialAllowanceRow = industrialAllowanceRow;
        if (furnitureFixtureRow.length > 0) next.furnitureFixtureRow = furnitureFixtureRow;
        if (computerHardSoftRow.length > 0) next.computerHardSoftRow = computerHardSoftRow;
        if (fullPayMotorRow.length > 0) next.fullPayMotorRow = fullPayMotorRow;
        if (hirePurchaseMotorRow.length > 0) next.hirePurchaseMotorRow = hirePurchaseMotorRow;
        if (machineryRow.length > 0) next.machineryRow = machineryRow;
        if (liftEscalatorRow.length > 0) next.liftEscalatorRow = liftEscalatorRow;
        if (equipmentRow.length > 0) next.equipmentRow = equipmentRow;
        
        // Always include ppeRate with annualId
        const ppeRateBase = p.ppeRate || {
            locationOfPropertyRate: p.locationOfPropertyRate ?? 4,
            commercialAllowanceRate: p.commercialAllowanceRate ?? 20,
            industrialAllowanceRate: p.industrialAllowanceRate ?? 20,
            furnitureFixtureRate: p.furnitureFixtureRate ?? 20,
            computerHardSoftRate: p.computerHardSoftRate ?? 20,
            fullPayMotorRate: p.fullPayMotorRate ?? 30,
            hirePurchaseMotorRate: p.hirePurchaseMotorRate ?? 30,
            machineryRate: p.machineryRate ?? 20,
            liftEscalatorRate: p.liftEscalatorRate ?? 20,
            equipmentRate: p.equipmentRate ?? 20,
        };
        
        // Add annualId to ppeRate
        next.ppeRate = {
            ...ppeRateBase,
            annualId: annualId
        };

        if (p.ppeTitle && typeof p.ppeTitle === 'object' && Object.keys(p.ppeTitle).length > 0) {
            next.ppeTitle = p.ppeTitle;
        }
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}

/**
 * PL-AE-DEP only: shared by PPE (xxxRow / ppeField) and ITL Taxi Depn (intangibleRows / iaField).
 * Does not replace {@link propertyPlantEquipmentStrategy} used by BS-NCA-PPE.
 */
export const depreciationSharedStrategy: PayloadStrategy = {
    buildPayload: (args) => {
        const p = (args.prev || {}) as any;
        if (Array.isArray(p.intangibleRows) && p.intangibleRows.length > 0) {
            return InvestmentInTaxiCabsAndLicensesStrategy.buildPayload(args);
        }
        return propertyPlantEquipmentStrategy.buildPayload(args);
    },
    readAmount: (payload: any) => propertyPlantEquipmentStrategy.readAmount(payload),
};

// Intangible Assets strategy (E1): ensure payload conforms to IntangibleAssetsWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const intangibleAssetsStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<IntangibleAssetsWorkingPaper>;
        const annualId = ctx?.annualId;

        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };

        const intangibleRows = addAnnualId(Array.isArray(p.intangibleRows) ? p.intangibleRows : []);

        const next: any = {};
        if (intangibleRows.length > 0) next.intangibleRows = intangibleRows;

        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}
